import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const SubscriptionModal = ({ userType, userId, onClose }) => {
  const { isDarkMode } = useTheme();
  const [subscriptionType, setSubscriptionType] = useState('weekly');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('userToken');

      let url, headers = { 'Content-Type': 'application/json' };

      if (userType === 'registered') {
        url = `${BACKEND_URL}/api/novayar/subscription/user`;
        headers['Authorization'] = `Bearer ${token}`;
      } else {
        url = `${BACKEND_URL}/api/novayar/subscription/guest/${userId}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          subscription_type: subscriptionType,
          receipt_data: null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'خطا در ثبت درخواست');
      }

      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
        }, 3000);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className={`rounded-3xl p-6 sm:p-8 max-w-md w-full text-center shadow-2xl transition-colors duration-300 ${
          isDarkMode 
            ? 'bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700' 
            : 'bg-white border border-gray-100'
        }`}>
          <div className="text-5xl sm:text-6xl mb-4">✅</div>
          <h3 className={`text-xl sm:text-2xl font-bold mb-4 transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-800'
          }`}>
            درخواست ثبت شد!
          </h3>
          <p className={`text-sm sm:text-base mb-6 transition-colors duration-300 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-600'
          }`}>
            درخواست اشتراک شما ثبت شد. کارشناس‌ها بررسی میکنن و بهتون اطلاع میدن.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className={`rounded-2xl sm:rounded-3xl p-6 sm:p-8 max-w-2xl w-full shadow-2xl my-8 transition-colors duration-300 ${
        isDarkMode 
          ? 'bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700' 
          : 'bg-white border border-gray-100'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className={`text-2xl sm:text-3xl font-bold transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-800'
          }`}>
            📋 درخواست اشتراک
          </h2>
          <button
            onClick={onClose}
            className={`transition-colors duration-300 ${
              isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* نوع اشتراک */}
          <div>
            <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              نوع اشتراک
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setSubscriptionType('weekly')}
                className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                  subscriptionType === 'weekly'
                    ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                    : isDarkMode
                    ? 'border-gray-600 hover:border-gray-500'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="text-2xl mb-2">📅</div>
                <div className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  یک هفته‌ای
                </div>
                <div className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  7 روز استفاده نامحدود
                </div>
              </button>

              <button
                type="button"
                onClick={() => setSubscriptionType('monthly')}
                className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                  subscriptionType === 'monthly'
                    ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                    : isDarkMode
                    ? 'border-gray-600 hover:border-gray-500'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="text-2xl mb-2">🗓️</div>
                <div className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  30 روزه
                </div>
                <div className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  30 روز استفاده نامحدود
                </div>
              </button>
            </div>
          </div>

          {/* توضیحات درخواست */}
          <div className={`${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gradient-to-r from-blue-50 to-purple-50'} rounded-xl p-6 border transition-colors duration-300`}>
            <h3 className={`font-bold text-lg mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              📝 مراحل بعدی
            </h3>
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} leading-relaxed`}>
              با ثبت درخواست، درخواست شما برای کارشناسان ارسال می‌شود و پس از بررسی، اشتراک شما فعال خواهد شد.
            </p>
          </div>

          {/* دکمه ثبت */}
          <div className="flex space-x-3 space-x-reverse pt-4">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-6 py-3 rounded-lg font-medium transition-colors ${
                isDarkMode
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
              }`}
              disabled={loading}
            >
              انصراف
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold px-6 py-3 rounded-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  در حال ثبت...
                </span>
              ) : (
                '✅ ثبت درخواست اشتراک'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubscriptionModal;
